import { normalizeUnitCode } from '../../utils/units';
import { formatProductDimensions } from '../../utils/productDimensions';
import type { LoadUnit } from './freightGoodsTable';
import { carriedLoadItems } from './supplierOrderFreight';
import type {
  TransportSupplierOrderLoadItemDto,
  TransportSupplierOrderReceiptDocumentDto,
  TransportSupplierOrderSummaryDto,
} from '../../types/transport';

/**
 * INBOUND truck: what each linked ZD was to bring and what has been received
 * through this truck's PZ — the ZD counterpart of the ZK realization card.
 * Presentation of backend facts only (single-transport GET):
 *  - Plan = what THIS truck carries of the line (`carried`, Addendum A: an
 *    entered quantity or the rest of the line); the whole line on an older
 *    response. A line the truck does not carry is left out;
 *  - Przyjęto = `loadItems[].received` (received, non-reversed PZ of this truck,
 *    backend utils/supplierOrderTransportReceipt.js);
 *  - Nie przyjęto = max(Plan − Przyjęto, 0).
 * Quantities are shown in the ZD line's own unit (pcs/m²/m³); a line whose
 * unit has no stored equivalent reads unknown, never 0. Non-financial.
 */
export type RealizationState = 'complete' | 'partial' | 'missing' | 'pending';

export interface SupplierOrderRealizationLine {
  id: string;
  name: string;
  dimensions: string | null;
  unit: LoadUnit | null;
  plan: number | null;
  /** `null` = unknown (older backend, or the unit was not entered on the PZ). */
  received: number | null;
  notReceived: number | null;
  /** 0–100; `null` when either side is unknown. */
  receivedPercent: number | null;
  state: RealizationState;
}

export interface SupplierOrderRealizationRow {
  id: string;
  label: string;
  supplierName: string | null;
  lines: SupplierOrderRealizationLine[];
  receiptDocuments: TransportSupplierOrderReceiptDocumentDto[];
  hasSharedReceiptDocument: boolean;
}

const QUANTITY_FIELD: Record<LoadUnit, 'quantityPcs' | 'quantityM2' | 'quantityM3'> = {
  pcs: 'quantityPcs',
  m2: 'quantityM2',
  m3: 'quantityM3',
};

const round3 = (value: number) => Math.round(value * 1000) / 1000;

function lineUnit(item: TransportSupplierOrderLoadItemDto): LoadUnit | null {
  const code = normalizeUnitCode(item.unit);
  if (code === 'pcs' || code === 'm2' || code === 'm3') return code;
  // A legacy unit (mb, kpl.): fall back to the first stored physical equivalent.
  return (['pcs', 'm2', 'm3'] as LoadUnit[]).find((unit) => (item[QUANTITY_FIELD[unit]] ?? 0) > 0) ?? null;
}

function buildLine(item: TransportSupplierOrderLoadItemDto, hasReceipt: boolean): SupplierOrderRealizationLine {
  const unit = lineUnit(item);
  const plan = unit ? item[QUANTITY_FIELD[unit]] ?? null : null;
  const received = unit && item.received ? item.received[unit] : null;
  const known = plan !== null && received !== null;
  const notReceived = known ? round3(Math.max(plan - received, 0)) : null;
  // Before any PZ came off the truck the line is still pending (figures stay,
  // no red "missing" bar for goods that are not due yet) — like a ZK before
  // delivery confirmation.
  const receivedPercent = hasReceipt && known && plan > 0
    ? Math.min(100, Math.max(0, (received / plan) * 100))
    : null;
  const state: RealizationState = receivedPercent === null
    ? 'pending'
    : receivedPercent >= 100
      ? 'complete'
      : receivedPercent > 0 ? 'partial' : 'missing';
  return {
    id: item.id,
    name: item.name,
    dimensions: formatProductDimensions({
      width: item.width ?? undefined,
      height: item.height ?? undefined,
      length: item.length ?? undefined,
      thickness: item.thickness ?? undefined,
      dimensionDisplayProfile: item.dimensionDisplayProfile,
    }),
    unit,
    plan,
    received,
    notReceived,
    receivedPercent,
    state,
  };
}

export function summarizeSupplierOrderRealization(
  supplierOrders: TransportSupplierOrderSummaryDto[] | undefined,
): SupplierOrderRealizationRow[] {
  return (supplierOrders || []).map((supplierOrder) => {
    const receiptDocuments = supplierOrder.receiptDocuments || [];
    return {
      id: supplierOrder.id,
      label: supplierOrder.displayOrderNumber || supplierOrder.orderNumber,
      supplierName: supplierOrder.supplierName,
      // A line this truck does not carry stays visible only when its PZ
      // still received something through this truck.
      lines: [
        ...carriedLoadItems(supplierOrder.loadItems),
        ...(supplierOrder.loadItems || []).filter((item) => item.carriedSource === 'NONE'
          && Object.values(item.received || {}).some((value) => Number(value) > 0))
          .map((item) => ({ ...item, quantity: 0, quantityPcs: 0, quantityM2: 0, quantityM3: 0 })),
      ].map((item) => buildLine(item, receiptDocuments.length > 0)),
      receiptDocuments,
      hasSharedReceiptDocument: receiptDocuments.some((document) => document.sharedWithOtherTransports),
    };
  });
}
